import { useEffect, useState } from 'react'
import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  NumberInput,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { COMMON_TIMEZONES, trackGaps, trackIdFromName } from '@racedash/shared'
import { countEventsForTrack, createTrack, deleteTrack, updateTrack, useTracks } from '../data/tracks'
import type { Track } from '../data/tracks'
import ConfirmDelete from '../components/ConfirmDelete'

/**
 * Circuits.
 *
 * A track is a record rather than a name because three different things need
 * something off it: a weather overlay needs the location, a full-course-yellow
 * estimate needs the length, and matching a live stream to a catalogued event
 * needs a stable identity. Only the last of those can be met by a string.
 *
 * Most rows arrive incomplete. The TSL scrape creates a circuit the moment it
 * meets one, because an event that cannot be catalogued is worse than a track
 * record with two empty fields — so this page is largely about finishing them
 * off, and says which are outstanding.
 */
export default function TracksPage() {
  const { tracks, error } = useTracks()
  const [editing, setEditing] = useState<Track | null>(null)
  const [adding, setAdding] = useState(false)
  const [removing, setRemoving] = useState<Track | null>(null)

  const incomplete = tracks?.filter((t) => trackGaps(t).length > 0) ?? []

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-start">
        <div>
          <Title order={1} size="h2">
            Circuits
          </Title>
          <Text c="dimmed" size="sm">
            Location and length are what the weather overlay and full-course-yellow estimates read.
            A circuit discovered by a scrape starts with neither.
          </Text>
        </div>
        <Button onClick={() => setAdding(true)}>New circuit</Button>
      </Group>

      {error && <Alert color="red">{error}</Alert>}

      {incomplete.length > 0 && (
        <Alert color="yellow">
          {incomplete.length} of {tracks?.length} {incomplete.length === 1 ? 'circuit is' : 'circuits are'}{' '}
          missing a location or a length. Features that need them are hidden for those circuits
          rather than shown with a guess.
        </Alert>
      )}

      {tracks && tracks.length > 0 && (
        <Paper withBorder>
          <Table.ScrollContainer minWidth={760}>
            <Table highlightOnHover verticalSpacing="xs" horizontalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Circuit</Table.Th>
                  <Table.Th w={150}>Id</Table.Th>
                  <Table.Th w={110}>Length</Table.Th>
                  <Table.Th w={160}>Location</Table.Th>
                  <Table.Th w={150}>Timezone</Table.Th>
                  <Table.Th w={150} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {tracks.map((t) => {
                  const gaps = trackGaps(t)
                  return (
                    <Table.Tr key={t.id}>
                      <Table.Td>
                        <Group gap="xs" wrap="nowrap">
                          <Text size="sm" fw={500}>
                            {t.name}
                          </Text>
                          {gaps.length > 0 && (
                            <Badge size="xs" color="yellow" variant="light" style={{ flex: 'none' }}>
                              needs {gaps.join(' & ')}
                            </Badge>
                          )}
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" c="dimmed" ff="monospace">
                          {t.id}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed" ff="monospace">
                          {t.lengthM ? `${(t.lengthM / 1000).toFixed(3)} km` : '—'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" c="dimmed" ff="monospace">
                          {t.location ? `${t.location.lat.toFixed(4)}, ${t.location.lng.toFixed(4)}` : '—'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {t.timezone}
                        </Text>
                      </Table.Td>
                      <Table.Td align="right">
                        <Group gap={4} justify="flex-end" wrap="nowrap">
                          <Button variant="subtle" size="compact-sm" onClick={() => setEditing(t)}>
                            Edit
                          </Button>
                          <Button
                            variant="subtle"
                            color="red"
                            size="compact-sm"
                            onClick={() => setRemoving(t)}
                          >
                            Delete
                          </Button>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  )
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Paper>
      )}
      {tracks?.length === 0 && (
        <Paper withBorder p="xl">
          <Text c="dimmed" ta="center">
            No circuits yet. They appear as the TSL scrape meets them, or add one by hand.
          </Text>
        </Paper>
      )}

      <ConfirmDelete
        opened={removing !== null}
        onClose={() => setRemoving(null)}
        what="circuit"
        name={removing?.name ?? ''}
        referenceNoun="event"
        countReferences={() => countEventsForTrack(removing!.id)}
        onDelete={() => deleteTrack(removing!.id)}
        note="The TSL scrape will recreate it as a stub if it names this circuit again, but the location and length would be lost."
      />

      <TrackDialog opened={adding} onClose={() => setAdding(false)} />
      <TrackDialog opened={editing !== null} onClose={() => setEditing(null)} track={editing ?? undefined} />
    </Stack>
  )
}

interface Draft {
  name: string
  lat: string
  lng: string
  lengthM: string
  timezone: string
  country: string
}

const EMPTY: Draft = { name: '', lat: '', lng: '', lengthM: '', timezone: 'Europe/London', country: '' }
const FIELDS = Object.keys(EMPTY) as (keyof Draft)[]

function TrackDialog({
  opened,
  onClose,
  track,
}: {
  opened: boolean
  onClose: () => void
  track?: Track
}) {
  const editing = Boolean(track)
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [baseline, setBaseline] = useState<Draft>(EMPTY)
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((d) => ({ ...d, [k]: v }))

  useEffect(() => {
    if (!opened) return
    setFailed(null)
    const next: Draft = track
      ? {
          name: track.name,
          lat: track.location ? String(track.location.lat) : '',
          lng: track.location ? String(track.location.lng) : '',
          lengthM: track.lengthM ? String(track.lengthM) : '',
          timezone: track.timezone,
          country: track.country ?? '',
        }
      : EMPTY
    setDraft(next)
    setBaseline(next)
  }, [opened, track])

  const changed = FIELDS.some((k) => draft[k] !== baseline[k])
  // A location is both halves or neither — half a coordinate positions nothing.
  const latOk = draft.lat.trim() === '' || Number.isFinite(Number(draft.lat))
  const lngOk = draft.lng.trim() === '' || Number.isFinite(Number(draft.lng))
  const bothOrNeither = (draft.lat.trim() === '') === (draft.lng.trim() === '')
  const complete = Boolean(draft.name.trim() && draft.timezone) && latOk && lngOk && bothOrNeither

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (busy || !complete || (editing && !changed)) return
    setBusy(true)
    setFailed(null)
    try {
      const input = {
        name: draft.name.trim(),
        location:
          draft.lat.trim() && draft.lng.trim()
            ? { lat: Number(draft.lat), lng: Number(draft.lng) }
            : null,
        lengthM: draft.lengthM.trim() ? Number(draft.lengthM) : null,
        timezone: draft.timezone,
        country: draft.country.trim().toUpperCase() || null,
      }
      if (track) {
        await updateTrack(track.id, input)
      } else {
        await createTrack(trackIdFromName(input.name), input)
      }
      onClose()
    } catch (err) {
      setFailed(err instanceof Error ? err.message : 'Could not save the circuit.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={() => !busy && onClose()}
      title={editing ? `Edit ${track?.name}` : 'New circuit'}
      size="lg"
    >
      <form onSubmit={(e) => void submit(e)}>
        <Stack gap="md">
          {failed && <Alert color="red">{failed}</Alert>}

          <TextInput
            label="Name"
            value={draft.name}
            onChange={(e) => set('name', e.currentTarget.value)}
            placeholder="Brands Hatch Indy"
            description={
              editing
                ? `Identifier: ${track?.id} — fixed, because events reference it.`
                : `Identifier will be: ${trackIdFromName(draft.name) || '…'}`
            }
            data-autofocus
            required
          />

          <Group grow align="flex-start">
            <TextInput
              label="Latitude"
              value={draft.lat}
              onChange={(e) => set('lat', e.currentTarget.value)}
              placeholder="51.3569"
              error={!latOk && 'Not a number'}
            />
            <TextInput
              label="Longitude"
              value={draft.lng}
              onChange={(e) => set('lng', e.currentTarget.value)}
              placeholder="0.2630"
              error={(!lngOk && 'Not a number') || (!bothOrNeither && 'Both or neither')}
            />
          </Group>
          <Text c="dimmed" size="xs" mt={-8}>
            Positions the weather overlay. Leave blank if unknown — the overlay is then hidden for
            this circuit rather than drawn somewhere wrong.
          </Text>

          <Group grow align="flex-start">
            <NumberInput
              label="Length (metres)"
              value={draft.lengthM}
              onChange={(v) => set('lengthM', String(v))}
              min={1}
              placeholder="1944"
              description="Used to estimate a lap under full course yellow."
            />
            <Select
              label="Timezone"
              data={COMMON_TIMEZONES}
              value={draft.timezone}
              onChange={(v) => set('timezone', v ?? 'Europe/London')}
              searchable
              allowDeselect={false}
              description="Seeds a new event's timezone."
            />
            <TextInput
              label="Country"
              value={draft.country}
              onChange={(e) => set('country', e.currentTarget.value)}
              placeholder="GB"
              maxLength={2}
            />
          </Group>

          <Group justify="flex-end" mt="xs">
            <Button variant="default" type="button" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" loading={busy} disabled={!complete || (editing && !changed)}>
              {editing ? 'Save changes' : 'Add circuit'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  )
}
